<script>
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { filtersConfigStore } from "@stores/filtersConfigStores.js";
  import { getApplicableFilters } from "../../../../lib/util/getApplicableFilters";
  import { pmfilters } from "lib/stores.js";
  import { useNavigate } from "svelte-navigator";
  import { ROUTES } from "@utils/routes.js";

  // ui-elements
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import Toggle from "lib/components/Toggle.svelte";
  import { Accordion, SectionWrapper, Button } from "paxray-components-svelte";
  import AppSelection from "./components/appSelection.svelte";
  import AppInformation from "./components/appInformation.svelte";
  import Header from "lib/components/Header.svelte";
  import { Loading } from "carbon-components-svelte";

  import Filter from "./components/filter/Filter.svelte";
  import FilterToast from "./components/FilterToast.svelte";
  import MatrixTable from "./components/MatrixTable.svelte";
  import ButtonNumberInput from "../../../../lib/components/ButtonNumberInput.svelte";
  import {
    ARROW_LEFT_ICON,
    FULLSCREEN_ICON,
    FULLSCREEN_OFF_ICON,
  } from "@utils/icons.js";

  const navigate = useNavigate();
  export let analysisid;
  const { setCurrentView } = filtersConfigStore;

  let currentApp = "";
  let windowPartitionsLoading = false;
  let applicationlist = [];
  let fullscreen = false;
  let selectedLevel = 0;
  let selectedIndex = 0;
  let loading = true;
  let loadingApp = true;
  let view = "Shortcuts";
  let showToast = false;
  let frequency = 0;

  $: setCurrentView(view);

  let from = "";
  let to = "";
  let selectedFrom = from;
  let selectedTo = to;
  let tableHeader;
  let dataTable = [];
  let copyPasteSnapshot = null;

  let data = [];

  $: {
    if ($pmfilters && $filtersConfigStore.storeLoaded && currentApp) {
      getData(selectedIndex);
    }
  }

  $: {
    if ($pmfilters && $filtersConfigStore.storeLoaded) {
      getWindowPartitions();
    }
  }

  $: if (selectedIndex !== 0 && currentApp) {
    selectedTo = currentApp;
  }

  $: if (selectedFrom || selectedTo) {
    filterDataTable();
  }

  function getWindowPartitions() {
    windowPartitionsLoading = true;
    loadingApp = true;
    const applicableFilters = getApplicableFilters($pmfilters[analysisid]);
    const params = new URLSearchParams({
      analysisid,
      filter: JSON.stringify(applicableFilters),
    });
    fetchWithSession(`/api/analysis/AppOverviewKPIs?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        applicationlist = data;
        if (!currentApp && data.length > 0) currentApp = data[0].id;
        windowPartitionsLoading = false;
        loadingApp = false;
      });
  }

  function getData(selectedIndex) {
    if (!$filtersConfigStore.storeLoaded) return;
    loading = true;
    const applicableFilters = getApplicableFilters($pmfilters[analysisid]);
    const filterString = JSON.stringify(applicableFilters);
    let endpoint = "";
    if (selectedIndex === 0) {
      endpoint = `/api/analysis/copyPasteAnalysis?analysisid=`;
    } else if (selectedIndex === 1) {
      endpoint = `/api/analysis/usedShortCutAnalysis?analysisid=`;
    } else {
      endpoint = `/api/analysis/missedShortCutAnalysis?analysisid=`;
    }
    const params = new URLSearchParams({
      analysisid,
      ...(selectedLevel
        ? { partitionlabel: currentApp }
        : { applicationname: currentApp }),
      mincount: selectedIndex === 0 ? "0" : String(frequency),
      filter: filterString,
    });
    fetchWithSession(`${endpoint.split("?")[0]}?${params.toString()}`)
      .then((res) => res.json())
      .then((dataRes) => {
        data = dataRes;
        if (data.length === 0 && filterString !== "[]") showToast = true;
        getInitialDataTable();
        loading = false;
      });
  }

  function shortcutTable() {
    tableHeader = ["Shortcut/App"];
    dataTable = [];

    // Get unique values
    const shortcuts = Array.from(new Set(data.map((item) => item.shortcut)));
    const apps = Array.from(new Set(data.map((item) => item.app)));

    // Add application columns to the header
    tableHeader.push(...apps);
    tableHeader.push("Total");

    // Init total by columns
    let columnTotals = Array(apps.length).fill(0);
    let grandTotal = 0;

    const rows = shortcuts.map((shortcut) => {
      const row = [shortcut];
      let rowTotal = 0;

      apps.forEach((app, idx) => {
        const count = data
          .filter((item) => item.shortcut === shortcut && item.app === app)
          .reduce((sum, item) => sum + item.count, 0);

        row.push(count > 0 ? count : "");

        columnTotals[idx] += count;
        rowTotal += count;
      });

      row.push(rowTotal);
      grandTotal += rowTotal;

      return row;
    });
    const totalRow = ["Total"];
    columnTotals.forEach((total) => totalRow.push(total));
    totalRow.push(grandTotal);

    dataTable = [...rows, totalRow];
  }

  function copyPasteTable(rebuild = true) {
    if (rebuild) {
      const allToApps = Array.from(new Set(data.map((item) => item.to_app)));
      const fromApps = Array.from(new Set(data.map((item) => item.from_app)));
      const rows = fromApps.map((fromApp) => {
        const row = [fromApp];
        let rowTotal = 0;

        allToApps.forEach((toApp) => {
          const totalCount = data
            .filter(
              (item) => item.from_app === fromApp && item.to_app === toApp,
            )
            .reduce((sum, item) => sum + (Number(item.count) || 0), 0);
          row.push(totalCount > 0 ? totalCount : "");
          rowTotal += totalCount;
        });

        row.push(rowTotal);
        return row;
      });

      const countColumns = ["Total"];
      allToApps.forEach((_, columnIndex) => {
        countColumns.push(
          rows.reduce(
            (sum, row) => sum + (Number(row[columnIndex + 1]) || 0),
            0,
          ),
        );
      });
      countColumns.push(
        rows.reduce((sum, row) => sum + (Number(row[row.length - 1]) || 0), 0),
      );

      copyPasteSnapshot = {
        headers: ["From/to", ...allToApps, "Total"],
        rows: [...rows, countColumns],
      };
    }

    if (!copyPasteSnapshot) return;
    const totalRow = copyPasteSnapshot.rows[copyPasteSnapshot.rows.length - 1];
    const visibleColumns = copyPasteSnapshot.headers
      .map((header, index) => ({ header, index }))
      .filter(
        ({ header, index }) =>
          index === 0 ||
          header === "Total" ||
          Math.max(
            0,
            ...copyPasteSnapshot.rows
              .slice(0, -1)
              .map((row) => Number(row[index]) || 0),
          ) >= frequency,
      );
    const visibleRows = copyPasteSnapshot.rows.filter(
      (row, rowIndex) =>
        rowIndex === copyPasteSnapshot.rows.length - 1 ||
        Math.max(...row.slice(1, -1).map((value) => Number(value) || 0)) >=
          frequency,
    );
    tableHeader = visibleColumns.map(({ header }) => header);
    dataTable = visibleRows.map((row) =>
      visibleColumns.map(({ index }) => row[index]),
    );
  }

  function getInitialDataTable() {
    if (selectedIndex === 0) {
      copyPasteTable();
    } else {
      shortcutTable();
    }
  }

  function fiterCopyPaste() {
    if (selectedTo === "From/to") {
      selectedFrom = "";
      selectedTo = "";
      getInitialDataTable();
      return;
    }

    let previousTableHeader = [...tableHeader];
    let previousDataTable = [...dataTable];
    let matches = [];
    let tableFrom = [];
    tableHeader = ["From/to"];
    dataTable = [];

    // Option 1: only selectedTo (click on column header)
    if (selectedTo && !selectedFrom) {
      to = selectedTo;

      data.forEach((element) => {
        if (element.to_app === to) {
          if (!tableHeader.includes(element.to_window)) {
            tableHeader.push(element.to_window);
          }
          if (!tableFrom.includes(element.from_app)) {
            tableFrom.push(element.from_app);
          }
          matches.push({
            from_window: element.from_app,
            to_window: element.to_window,
            count: element.count,
          });
        }
      });
    }

    // Option 2: only selectedFrom (click on row header)
    else if (selectedFrom && !selectedTo) {
      from = selectedFrom;

      data.forEach((element) => {
        if (element.from_app === from) {
          if (!tableHeader.includes(element.to_app)) {
            tableHeader.push(element.to_app);
          }
          if (!tableFrom.includes(element.from_window)) {
            tableFrom.push(element.from_window);
          }
          matches.push({
            from_window: element.from_window,
            to_window: element.to_app,
            count: element.count,
          });
        }
      });
    }

    // Option 3: both selectedFrom and selectedTo (click on cell)
    else if (selectedFrom && selectedTo) {
      from = selectedFrom;
      to = selectedTo;

      data.forEach((element) => {
        if (element.from_app === from && element.to_app === to) {
          if (!tableHeader.includes(element.to_window)) {
            tableHeader.push(element.to_window);
          }
          if (!tableFrom.includes(element.from_window)) {
            tableFrom.push(element.from_window);
          }
          matches.push({
            from_window: element.from_window,
            to_window: element.to_window,
            count: element.count,
          });
        }
      });
    }

    if (matches.length === 0) {
      dataTable = previousDataTable;
      tableHeader = previousTableHeader;
      return;
    }

    // build the table
    tableHeader.push("Total");
    let countColumns = ["Total", ...Array(tableHeader.length - 2).fill(0), 0];

    tableFrom.forEach((fromWin) => {
      let count = 0;
      let row = [fromWin];
      for (let i = 1; i < tableHeader.length - 1; i++) {
        const toWin = tableHeader[i];
        const valueList = matches.filter(
          (item) => item.from_window === fromWin && item.to_window === toWin,
        );
        const c = valueList.reduce((sum, item) => sum + item.count, 0);
        row.push(c > 0 ? c : "");
        count += c;
        countColumns[i] += c;
      }
      row.push(count);
      countColumns[countColumns.length - 1] += count;
      dataTable.push(row);
    });

    dataTable.push(countColumns);
  }

  function filterShortcuts() {
    if (selectedTo === "Shortcut/App") {
      selectedFrom = "";
      selectedTo = "";
      shortcutTable();
      return;
    }

    let previousTableHeader = [...tableHeader];
    let previousDataTable = [...dataTable];
    let matches = [];
    let rowKeys = [];
    tableHeader = ["Shortcut/App"];
    dataTable = [];

    // Option 1: only column selected (shortcut)
    if (selectedTo && !selectedFrom) {
      const selectedApp = selectedTo;

      data.forEach((entry) => {
        if (entry.app === selectedApp) {
          if (!tableHeader.includes(entry.windowtitle)) {
            tableHeader.push(entry.windowtitle);
          }
          if (!rowKeys.includes(entry.shortcut)) {
            rowKeys.push(entry.shortcut);
          }
          matches.push({
            rowKey: entry.shortcut,
            colKey: entry.windowtitle,
            count: entry.count,
          });
        }
      });
    }

    // Option 2: only row selected (shortcut)
    else if (selectedFrom && !selectedTo) {
      const selectedShortcut = selectedFrom;

      data.forEach((entry) => {
        if (entry.shortcut === selectedShortcut) {
          if (!tableHeader.includes(entry.app)) {
            tableHeader.push(entry.app);
          }
          if (!rowKeys.includes(entry.windowtitle)) {
            rowKeys.push(entry.windowtitle);
          }
          matches.push({
            rowKey: entry.windowtitle,
            colKey: entry.app,
            count: entry.count,
          });
        }
      });
    }

    // Option 3: cell selected (shortcut + app)
    else if (selectedFrom && selectedTo) {
      const selectedShortcut = selectedFrom;
      const selectedApp = selectedTo;

      data.forEach((entry) => {
        if (entry.shortcut === selectedShortcut && entry.app === selectedApp) {
          if (!tableHeader.includes(entry.windowtitle)) {
            tableHeader.push(entry.windowtitle);
          }
          if (!rowKeys.includes(entry.shortcut)) {
            rowKeys.push(entry.shortcut);
          }
          matches.push({
            rowKey: entry.shortcut,
            colKey: entry.windowtitle,
            count: entry.count,
          });
        }
      });
    }

    if (matches.length === 0) {
      dataTable = previousDataTable;
      tableHeader = previousTableHeader;
      return;
    }

    tableHeader.push("Total");
    let countColumns = ["Total", ...Array(tableHeader.length - 2).fill(0), 0];

    rowKeys.forEach((rowKey) => {
      let count = 0;
      let row = [rowKey];
      for (let i = 1; i < tableHeader.length - 1; i++) {
        const colKey = tableHeader[i];
        const valueList = matches.filter(
          (item) => item.rowKey === rowKey && item.colKey === colKey,
        );
        const c = valueList.reduce((sum, item) => sum + item.count, 0);
        row.push(c > 0 ? c : "");
        count += c;
        countColumns[i] += c;
      }
      row.push(count);
      countColumns[countColumns.length - 1] += count;
      dataTable.push(row);
    });

    dataTable.push(countColumns);
  }

  function filterDataTable() {
    if (selectedIndex === 0) {
      fiterCopyPaste();
    } else {
      currentApp = selectedTo;
      filterShortcuts();
    }
  }

  function getApplist(applist) {
    return applist
      .map((app) => applicationlist.find((a) => a.clearname === app))
      .filter((app) => app !== undefined);
  }

  function handleMouseDown() {
    if (selectedIndex === 0) {
      copyPasteTable(false);
      if (selectedFrom || selectedTo) filterDataTable();
      return;
    }
    getData(selectedIndex);
  }
</script>

<FilterToast bind:showToast />

<Header section="Usage Analysis" breadcrumb="Shortcut Analysis">
  <Button
    text="To Dashboard"
    on:click={() => {
      navigate(`${ROUTES.analysis.dashboard}?analysisid=${analysisid}`);
    }}
    iconLeft={true}
    icon={ARROW_LEFT_ICON}
    kind="secondary"
  />
  <Filter />
</Header>

<SectionWrapper {fullscreen} fitScreen={true}>
  <div slot="main" class="main_wapper">
    <div class="canvas">
      <div class="ui_viewer_wrapper">
        {#if loading}
          <div class="loading_wrapper">
            <Loading withOverlay={false} />
          </div>
        {:else}
          <MatrixTable
            marginBottom="5vh"
            header={tableHeader}
            rows={dataTable}
            {loading}
            bind:to={selectedTo}
            bind:from={selectedFrom}
            disableFinalField
            boldFirstHeader={false}
            rowHeaderPadding="0.45rem 0.65rem"
          />
        {/if}
      </div>
    </div>
    <div class="menu_overlay bottom">
      <!-- <div class="buttons_wrapper">
        <ContentSwitcher
          bind:selected={selectedLevel}
          options={["Applications", "Windows", "Buttons"]}
          fitContent={true}
          disabled={disabledlevels}
        />
      </div> -->
      <div class="buttons_wrapper">
        <ContentSwitcher
          bind:selected={selectedIndex}
          options={["Copy & Paste", "Used Shortcuts"]}
          fitContent={true}
        />
      </div>
      <ButtonNumberInput
        buttonText="Min Frequency"
        bind:value={frequency}
        {handleMouseDown}
      />

      <div class="buttons_wrapper">
        {#if fullscreen === false}
          <Button
            kind="secondary"
            icon={FULLSCREEN_ICON}
            on:click={() => {
              fullscreen = true;
            }}
          />
        {:else}
          <Button
            kind="secondary"
            icon={FULLSCREEN_OFF_ICON}
            on:click={() => {
              fullscreen = false;
            }}
          />
        {/if}
      </div>
    </div>
  </div>

  <div class="menu" slot="menu">
    <Accordion heading="App Selection" open={true}>
      <AppSelection
        appData={applicationlist}
        bind:currentApp
        bind:selectedLevel
        windowSelectionEnabled={false}
        enableDeselection={false}
        loading={loadingApp}
      />
    </Accordion>
    <Accordion heading="App Information" open={true}>
      <AppInformation
        summary
        appData={applicationlist}
        {currentApp}
        loading={loadingApp}
      />
    </Accordion>

    <!-- <Accordion heading="Settings" open={false}>
      <div class="toggle_wrapper">
        <Toggle text="UI-Elements" bind:active={drawUIElements} />
        <Toggle text="Clusters" bind:active={drawClicks} />
        <Toggle text="Screenshot" bind:active={drawScreenshot} />
        <div class="upload_wrapper">
          <Button
            kind="secondary"
            iconLeft={true}
            enlarge={true}
            icon={UPLOAD_ICON}
            text="Upload Screenshot"
            disable={true}
          />
        </div>
      </div>
    </Accordion> -->
  </div>
</SectionWrapper>

<style>
  /* .upload_wrapper {
    padding-top: 1rem;
    margin-top: 1rem;
    border-top: 1px solid #d9d9d9;
  }

  .toggle_wrapper {
    display: flex;
    flex-direction: column;
  } */

  .main_wapper {
    position: relative;
    height: 100%;
    width: 100%;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .menu_overlay {
    position: absolute;
    width: 100%;
    z-index: 1; /* Stellt sicher, dass das Overlay über dem Canvas liegt */
    display: flex;
    justify-content: space-between;
    gap: 3rem;
    align-items: center;
  }

  .bottom {
    bottom: 0;
  }

  .canvas {
    height: calc(100% - 110px);
    width: 100%;
    z-index: 0; /* Platziert das Canvas hinter den Overlays */
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .buttons_wrapper {
    display: flex;
    gap: 0.5rem;
    user-select: none;
  }

  .menu {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: calc(100vh - 6rem);
    overflow-x: scroll;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  .ui_viewer_wrapper {
    min-height: 100%;
    max-height: 100%;
    width: 100%;
    max-width: 100%;
    overflow: visible;
  }

  .loading_wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80vh;
    width: 100%;
  }
</style>
